import clsx from 'clsx';

/**
 * The Imsenta mark.
 *
 * IMS and Centre: one long arc gathering two shorter ones around a solid core —
 * several management systems held in a single frame. The asymmetry is
 * deliberate; an even ring reads as a loading spinner rather than a mark.
 *
 * Drawn with `currentColor` so it takes the colour of whatever it sits in, and
 * built from strokes with round caps so it stays legible down to 16px.
 */
export function LogoMark({ className, title }: { className?: string; title?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {title ? <title>{title}</title> : null}
      <g stroke="currentColor" strokeWidth="5.8" strokeLinecap="round">
        <path d="M22.38 39.42A15.5 15.5 0 0 1 25.08 8.54" />
        <path d="M32.21 10.86A15.5 15.5 0 0 1 39.16 20.78" />
        <path d="M38.9 28.27A15.5 15.5 0 0 1 31.28 37.69" />
      </g>
      <circle cx="24" cy="24" r="5.4" fill="currentColor" />
    </svg>
  );
}

/** The mark on a rounded brand tile — favicons, avatars, app icons. */
export function LogoTile({ className, size = 28 }: { className?: string; size?: number }) {
  return (
    <span
      className={clsx('inline-grid shrink-0 place-items-center rounded-[22%] bg-brand-600 text-white', className)}
      style={{ width: size, height: size }}
    >
      <LogoMark className="size-[72%]" />
    </span>
  );
}

/**
 * Mark plus wordmark.
 *
 * The wordmark is live text rather than paths so it inherits the interface font
 * and stays crisp at any size; the tracking is tightened to sit with the mark.
 */
export function Logo({
  className,
  markClassName,
  wordmark = true,
  size = 28,
}: {
  className?: string;
  markClassName?: string;
  wordmark?: boolean;
  size?: number;
}) {
  return (
    <span className={clsx('inline-flex items-center gap-2.5', className)}>
      <span className="shrink-0 text-brand-600" style={{ width: size, height: size }}>
        <LogoMark className={clsx('size-full', markClassName)} title="Imsenta" />
      </span>
      {wordmark ? (
        <span className="font-semibold tracking-[-0.022em] text-ink-900" style={{ fontSize: size * 0.72 }}>
          Imsenta
        </span>
      ) : null}
    </span>
  );
}
